import { Address, UserAddress } from '@parkingbase/ecommerce-api-dev';
import { AddressService } from '@/services/address.service';

import { create } from 'zustand';
import { logger } from '@/lib/logger';

interface AddressState {
  addresses: Address[];
  isLoading: boolean;
  getAllAddresses: () => Promise<void>;
  setAddresses: (addresses: Address[]) => void;
  reset: () => void;
  createAddress: (address: UserAddress) => Promise<UserAddress | null>;
  updateAddress: (address: UserAddress) => Promise<UserAddress | null>;
  deleteAddress: (id: string) => Promise<void>;
}

export const useAddressStore = create<AddressState>((set, get) => ({
  addresses: [],
  isLoading: false,
  setAddresses: (addresses: Address[]) => set({ addresses }),
  reset: () => set({ addresses: [], isLoading: false }),
  getDefaultAddress: () => {
    const addresses = get().addresses;
    return (
      (addresses || []).find((address) => address.defaultAddress) ||
      (addresses?.length > 0 ? addresses[0] : null)
    );
  },
  getAllAddresses: async () => {
    try {
      set({ isLoading: true });
      const addressService = new AddressService();
      const addresses = await addressService.getUserAddress();
      if (addresses?.length > 0) {
        addresses.sort((a, b) => (a.defaultAddress ? -1 : 1));
      }
      set({ addresses });
    } catch (error) {
      logger.error('Failed to fetch user addresses', error, {
        feature: 'address-store',
        route: 'getAllAddresses',
      });
    } finally {
      set({ isLoading: false });
    }
  },
  createAddress: async (address: UserAddress) => {
    try {
      set({ isLoading: true });
      const addressService = new AddressService();
      const newAddress = await addressService.createUserAddress(address);
      if (newAddress) {
        await get().getAllAddresses();
      }
      return newAddress;
    } catch (error) {
      logger.error('Failed to create user address', error, {
        feature: 'address-store',
        route: 'createAddress',
      });
      return null;
    } finally {
      set({ isLoading: false });
    }
  },
  updateAddress: async (address: UserAddress) => {
    try {
      set({ isLoading: true });
      const addressService = new AddressService();
      const updatedAddress = await addressService.updateUserAddress(address);
      if (updatedAddress) {
        await get().getAllAddresses();
      }
      return updatedAddress;
    } catch (error) {
      logger.error('Failed to update user address', error, {
        feature: 'address-store',
        route: 'updateAddress',
      });
      return null;
    } finally {
      set({ isLoading: false });
    }
  },
  deleteAddress: async (id: string) => {
    try {
      set({ isLoading: true });
      const addressService = new AddressService();
      await addressService.deleteUserAddress(id);
      await get().getAllAddresses();
    } catch (error) {
      logger.error('Failed to delete user address', error, {
        feature: 'address-store',
        route: 'deleteAddress',
        id,
      });
    } finally {
      set({ isLoading: false });
    }
  },
}));
